import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import moment from 'moment';

export const generateAndDownloadReceipt = async (transaction: any) => {
  try {
    // Build receipt HTML
    const receiptHTML = buildReceiptHTML(transaction);

    // Create a temporary container to render HTML
    const container = document.createElement('div');
    container.innerHTML = receiptHTML;
    container.style.position = 'absolute';
    container.style.left = '-10000px';
    container.style.width = '210mm'; // A4 width
    container.style.backgroundColor = '#f5f5f5';
    document.body.appendChild(container);

    // Convert HTML to canvas
    const canvas = await html2canvas(container, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff'
    });

    // Remove temporary container
    document.body.removeChild(container);

    // Create PDF from canvas
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    const imgWidth = 210; // A4 width in mm
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= 297; // A4 height in mm

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= 297;
    }

    // Download PDF
    pdf.save(`receipt-${transaction.id}-${moment().format('YYYY-MM-DD')}.pdf`);
  } catch (error) {
    console.error('Error generating receipt PDF:', error);
    throw error;
  }
};

const buildReceiptHTML = (transaction: any): string => {
  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Receipt - ${transaction.id}</title>
  <style>
    body {
      font-family: Arial, sans-serif;
      margin: 0;
      padding: 40px;
      background-color: #ffffff;
    }
    .receipt-container {
      background-color: white;
      padding: 40px;
      border-radius: 8px;
    }
    .header {
      text-align: center;
      margin-bottom: 40px;
      border-bottom: 2px solid #e0e0e0;
      padding-bottom: 20px;
    }
    .header h1 {
      margin: 0 0 5px 0;
      color: #333;
      font-size: 28px;
    }
    .header p {
      margin: 5px 0;
      color: #666;
      font-size: 14px;
    }
    .section {
      margin-bottom: 30px;
    }
    .section-title {
      font-weight: bold;
      color: #333;
      margin-bottom: 10px;
      font-size: 14px;
      text-transform: uppercase;
      letter-spacing: 1px;
    }
    .section-content {
      color: #555;
      font-size: 14px;
      line-height: 1.8;
    }
    .row {
      display: flex;
      justify-content: space-between;
      margin-bottom: 8px;
    }
    .label {
      font-weight: 600;
      color: #333;
    }
    .value {
      color: #666;
      text-align: right;
    }
    .amount-row {
      display: flex;
      justify-content: space-between;
      margin-bottom: 15px;
      padding: 15px;
      background-color: #f9f9f9;
      border-radius: 4px;
    }
    .amount-label {
      font-weight: bold;
      color: #333;
      font-size: 16px;
    }
    .amount-value {
      font-weight: bold;
      color: #2e7d32;
      font-size: 16px;
    }
    .status-badge {
      display: inline-block;
      padding: 6px 12px;
      border-radius: 4px;
      font-size: 12px;
      font-weight: 600;
      text-transform: uppercase;
    }
    .status-settled {
      background-color: #e8f5e9;
      color: #2e7d32;
    }
    .status-pending {
      background-color: #fff3e0;
      color: #f57c00;
    }
    .status-failed {
      background-color: #ffebee;
      color: #c62828;
    }
    .divider {
      background-color: #e0e0e0;
      height: 1px;
      margin: 30px 0;
    }
    .footer {
      text-align: center;
      color: #999;
      font-size: 12px;
      margin-top: 40px;
    }
    .card-info {
      background-color: #f5f5f5;
      padding: 12px;
      border-radius: 4px;
      margin-top: 5px;
    }
  </style>
</head>
<body>
  <div class="receipt-container">
    <div class="header">
      <h1>RECEIPT</h1>
      <p>Transaction ID: <strong>${transaction.id}</strong></p>
    </div>

    <div class="section">
      <div class="section-title">Transaction Details</div>
      <div class="section-content">
        <div class="row">
          <span class="label">Date:</span>
          <span class="value">${moment(transaction.createdAt).format('MMMM D, YYYY [at] h:mm A')}</span>
        </div>
        <div class="row">
          <span class="label">Status:</span>
          <span class="value">
            <span class="status-badge status-${transaction.status}">
              ${transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1)}
            </span>
          </span>
        </div>
        <div class="row">
          <span class="label">Transaction Type:</span>
          <span class="value">${transaction.type ? transaction.type.toUpperCase() : 'Sale'}</span>
        </div>
      </div>
    </div>

    <div class="section">
      <div class="section-title">Billing Information</div>
      <div class="section-content">
        <div class="row">
          <span class="label">Plan ID:</span>
          <span class="value">${transaction.planId || 'N/A'}</span>
        </div>
        <div class="row">
          <span class="label">Subscription ID:</span>
          <span class="value">${transaction.subscriptionId || 'N/A'}</span>
        </div>
        ${transaction.subscription ? `
          <div class="row">
            <span class="label">Billing Period:</span>
            <span class="value">${moment(transaction.subscription.billingPeriodStartDate).format('MMM D, YYYY')} - ${moment(transaction.subscription.billingPeriodEndDate).format('MMM D, YYYY')}</span>
          </div>
        ` : ''}
      </div>
    </div>

    <div class="amount-row">
      <span class="amount-label">Total Amount:</span>
      <span class="amount-value">$${parseFloat(transaction.amount).toFixed(2)}</span>
    </div>

    <div class="section">
      <div class="section-title">Payment Method</div>
      <div class="section-content">
        ${transaction.creditCard ? `
          <div class="row">
            <span class="label">Card Type:</span>
            <span class="value">${transaction.creditCard.cardType}</span>
          </div>
          <div class="row">
            <span class="label">Card Number:</span>
            <span class="value">•••• •••• •••• ${transaction.creditCard.last4}</span>
          </div>
          <div class="row">
            <span class="label">Expiration:</span>
            <span class="value">${transaction.creditCard.expirationMonth}/${transaction.creditCard.expirationYear}</span>
          </div>
        ` : '<p>Card details not available</p>'}
      </div>
    </div>

    <div class="section">
      <div class="section-title">Merchant Information</div>
      <div class="section-content">
        <div class="row">
          <span class="label">Merchant Name:</span>
          <span class="value">${transaction.merchantAccountId || 'Stafflows'}</span>
        </div>
      </div>
    </div>

    ${transaction.customer && transaction.customer.firstName ? `
      <div class="divider"></div>
      <div class="section">
        <div class="section-title">Customer Information</div>
        <div class="section-content">
          <div class="row">
            <span class="label">Name:</span>
            <span class="value">${transaction.customer.firstName} ${transaction.customer.lastName || ''}</span>
          </div>
          ${transaction.customer.email ? `
            <div class="row">
              <span class="label">Email:</span>
              <span class="value">${transaction.customer.email}</span>
            </div>
          ` : ''}
        </div>
      </div>
    ` : ''}

    <div class="divider"></div>

    <div class="footer">
      <p>This is a computer-generated receipt. Please keep this for your records.</p>
      <p>Generated on ${moment().format('MMMM D, YYYY [at] h:mm A')}</p>
    </div>
  </div>
</body>
</html>
  `.trim();
};
